import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  createContext,
} from 'react';
import styled from 'styled-components';
import { Divider, Typography } from '@mui/material';
import { NavLink, NavLinkProps, useLocation } from 'react-router-dom';
import { isInSaaSMode } from '@elementary/common-ui/utils/common';
import { ReactComponent as SlackIcon } from '../../assets/20x20/slack.svg';
import { ReactComponent as DocsIcon } from '../../assets/20x20/docs.svg';
import { ReactComponent as SupportIcon } from '../../assets/20x20/support.svg';
import { ReactComponent as NewTag } from '../../assets/new-tag.svg';
import { ReactComponent as CloudIcon } from '../../assets/16x16/cloud.svg';
import usePersistSearchParams from '../../hooks/usePersistSearchParams';
import { Flex, FlexColumn } from '../Flex';
import Menu from '../Menu';
import Tab from '../Tab';
import Button from '../Button';
import { HEADER_HEIGHT } from '../../constants';
import { getTextColoredIconRules } from '../../style/iconColors';
import { fontSize, fontWeight, space, componentToken } from '../../style/utils';
import AvailableInCloudTooltip from './AvailableInCloudTooltip';
import NavbarTooltip from './NavBarTooltip';

export const NAVBAR_WIDTH = 230;
// Collapsed navbar width is equal to the header height to make sure it aligns with the logo in the header
export const COLLAPSED_NAVBAR_WIDTH = HEADER_HEIGHT;

// Created to make sure only one tooltip is open at a time
// For report usage with only available in cloud tooltips
const TooltipContext = createContext<{
  openTooltip: string | null;
  setOpenTooltip: (id: string | null) => void;
} | null>(null);

const Wrapper = styled(FlexColumn)<{
  $isExpanded: boolean;
  $isHoverExpanded: boolean;
  $isDisabled?: boolean;
}>`
  height: 100%;
  width: ${({ $isExpanded }) =>
    $isExpanded ? NAVBAR_WIDTH : COLLAPSED_NAVBAR_WIDTH}px;
  min-width: ${({ $isExpanded }) =>
    $isExpanded ? NAVBAR_WIDTH : COLLAPSED_NAVBAR_WIDTH}px;
  border-right: 1px solid ${componentToken('navbar.border')};
  justify-content: space-between;
  background: ${componentToken('navbar.background')};
  transition: width 120ms, min-width 120ms, box-shadow 120ms ease-out;
  padding-top: ${space('sm')};
  overflow: hidden;
  position: ${({ $isHoverExpanded }) =>
    $isHoverExpanded ? 'absolute' : 'relative'};
  top: ${({ $isHoverExpanded }) => ($isHoverExpanded ? 0 : 'auto')};
  left: ${({ $isHoverExpanded }) => ($isHoverExpanded ? 0 : 'auto')};
  z-index: ${({ $isHoverExpanded }) => ($isHoverExpanded ? 10 : 'auto')};
  box-shadow: ${({ $isHoverExpanded }) =>
    $isHoverExpanded ? componentToken('navbar.expandedShadow') : 'none'};
`;

const NavbarContainer = styled.div<{
  $isExpanded: boolean;
}>`
  position: relative;
  height: 100%;
  width: ${({ $isExpanded }) =>
    $isExpanded ? NAVBAR_WIDTH : COLLAPSED_NAVBAR_WIDTH}px;
  min-width: ${({ $isExpanded }) =>
    $isExpanded ? NAVBAR_WIDTH : COLLAPSED_NAVBAR_WIDTH}px;
  z-index: ${({ theme }) => theme.zIndex.drawer + 4};
`;

const TabLabel = styled(Typography)<{
  $isActive?: boolean;
  $isExpanded?: boolean;
}>`
  visibility: ${({ $isExpanded }) => ($isExpanded ? 'visible' : 'hidden')};
  font-weight: ${({ $isActive, theme }) =>
    $isActive ? theme.typography.fontWeightMedium : fontWeight('regular')};
`;

const TabContentWrapper = styled(Flex)<{
  $isActive?: boolean;
  $isDisabled?: boolean;
}>`
  width: 100%;
  min-width: ${NAVBAR_WIDTH}px;
  padding: ${space('mdMinus', 'lg')};
  background: ${({ $isActive, theme }) =>
    $isActive ? theme.palette.surface.active : 'none'};
  align-items: center;
  gap: ${space('lg')};
  cursor: pointer;
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.4 : 1)};
  border-left: ${({ $isActive, theme }) =>
    $isActive
      ? `2px solid ${theme.palette.accent.secondary}`
      : '2px solid transparent'};

  :hover {
    background: ${({ theme, $isActive }) =>
      !$isActive && theme.palette.surface.muted};
  }

  ${({ theme, $isActive }) => {
    const { icon, selectedIcon } = theme.tokens.components.navbar;
    const color = $isActive ? selectedIcon : icon;
    return (
      color &&
      `
      svg { color: ${color}; }
      ${getTextColoredIconRules('&', theme)}
    `
    );
  }}
`;

const BottomSection = styled(FlexColumn)`
  border-top: ${({ theme }) => `1px solid ${theme.palette.border.divider}`};
`;

const SectionTitleWrapper = styled(Typography)<{ $isExpanded: boolean }>`
  color: ${({ theme }) => theme.palette.content.disabled};
  font-size: ${fontSize('sm')};
  margin-top: ${space('lg')};
  padding: ${space('xs', 'xl')};
  font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
  white-space: nowrap;
  visibility: ${({ $isExpanded }) => ($isExpanded ? 'visible' : 'hidden')};
`;

const NewTagStyled = styled(NewTag)`
  position: absolute;
  right: 20px;
  top: 16px;
`;

// "Join the community" is not AI, so a look may draw it without the AI pink.
const JoinTheCommunityButton = styled(Button)`
  border: 1px solid ${componentToken('navbar.community.border')};
  border-radius: ${componentToken('navbar.community.radius')};
  color: ${componentToken('navbar.community.text')};
  background: ${componentToken('navbar.community.background')};

  .MuiButton-startIcon path {
    fill: ${componentToken('navbar.community.icon')};
  }

  padding-inline: 16px;
  margin-inline: 4px;
  gap: ${space('sm')};
  width: 100%;
`;

const CloudIconStyled = styled(CloudIcon)`
  margin-left: auto;
`;

const SectionTitle = React.forwardRef(
  ({ title, isExpanded }: { title: string; isExpanded: boolean }, ref: any) => (
    <SectionTitleWrapper ref={ref} $isExpanded={isExpanded}>
      {title}
    </SectionTitleWrapper>
  ),
);

type NavTabProps = NavLinkProps & {
  title: string;
  desc: string;
  isActive: boolean;
  activeIcon: React.ReactNode;
  isDisabled?: boolean;
  tooltip?: string;
  isExpanded: boolean;
  isHoverExpanded?: boolean;
  isNew?: boolean;
  formatPath: (to: string) => string;
  isSaasPage?: boolean;
};

const TabContent = React.forwardRef(
  (
    {
      to,
      title,
      desc,
      isActive,
      activeIcon,
      isDisabled,
      tooltip,
      isExpanded,
      isHoverExpanded,
      isNew,
      formatPath,
      isSaasPage,
    }: NavTabProps,
    ref: any,
  ) => {
    const isInSaaS = isInSaaSMode();

    if (isSaasPage) {
      return (
        <AvailableInCloudTooltip
          link={to.toString()}
          description={tooltip}
          tooltipId={title}
          TooltipContext={TooltipContext}
        >
          <TabContentWrapper>
            {activeIcon}
            <Flex sx={{ alignItems: 'center', flex: 1 }}>
              <TabLabel $isExpanded={isExpanded}>{title}</TabLabel>
              <CloudIconStyled />
            </Flex>
          </TabContentWrapper>
        </AvailableInCloudTooltip>
      );
    }

    return (
      <NavLink
        style={{
          textDecoration: 'none',
          minWidth: '100%',
        }}
        ref={ref}
        to={isDisabled ? '' : formatPath(to.toString())}
        onClick={(e) => {
          if (isDisabled || isSaasPage) {
            e.preventDefault();
          }
        }}
      >
        <NavbarTooltip
          title={title}
          subTitle={desc}
          isExpanded={isExpanded}
          isHoverExpanded={isHoverExpanded}
          isNew={isNew}
          disabled={!isInSaaS}
        >
          <TabContentWrapper $isActive={isActive} $isDisabled={isDisabled}>
            {activeIcon}
            <FlexColumn>
              <TabLabel $isActive={isActive} $isExpanded={isExpanded}>
                {title}
              </TabLabel>
            </FlexColumn>
          </TabContentWrapper>
        </NavbarTooltip>
      </NavLink>
    );
  },
);

type NavbarRoute = {
  key: string;
  path: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  hidden?: boolean;
  disabled?: boolean;
  tooltip?: React.ReactNode;
  isNew?: boolean;
  isSaasPage?: boolean;
};

type NavbarTitle = string;

export type NavbarItems = { [key: NavbarTitle]: NavbarRoute[] };

type Props = {
  isExpanded: boolean;
  items: NavbarItems;
  onSupportClick?: () => void;
  onExpand?: () => void;
};

const Navbar = ({
  isExpanded = false,
  items,
  onSupportClick,
  onExpand,
}: Props) => {
  const { getPathWithStoredParams } = usePersistSearchParams();
  const isInSaaS = isInSaaSMode();
  const SIDEBAR_EXPAND_DELAY_MS = 200;
  const expandTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const isHoverExpanded = !isExpanded && isHovered;
  const effectivelyExpanded = isExpanded || isHoverExpanded;

  useEffect(() => {
    if (isExpanded) {
      setIsHovered(false);
      if (expandTimeoutRef.current) {
        clearTimeout(expandTimeoutRef.current);
        expandTimeoutRef.current = null;
      }
    }
  }, [isExpanded]);

  useEffect(
    () => () => {
      if (expandTimeoutRef.current) clearTimeout(expandTimeoutRef.current);
    },
    [],
  );

  const handleHelpMenuItemClick = useCallback((url: string) => {
    window.open(url);
  }, []);

  const { pathname } = useLocation();

  const helpMenuOptions = useMemo(() => {
    const baseMenu = [
      {
        value:
          'https://github.com/elementary-data/elementary/issues/new?assignees=&labels=feature%2C+help+wanted&template=feature_request.md&title=',
        label: 'Feature request',
      },
      {
        value:
          'https://github.com/elementary-data/elementary/issues/new?assignees=&labels=bug&template=bug_report.md&title=',
        label: 'Report an issue',
      },
    ];

    const slackOption = {
      value: 'https://elementary-data.com/community',
      label: 'Slack community support',
    };

    if (isInSaaS) {
      return [...baseMenu, slackOption];
    } else {
      return [slackOption, ...baseMenu];
    }
  }, [isInSaaS]);

  const [openTooltip, setOpenTooltip] = useState<string | null>(null);

  return (
    <NavbarContainer $isExpanded={isExpanded}>
      <Wrapper
        $isExpanded={effectivelyExpanded}
        $isHoverExpanded={isHoverExpanded}
        onMouseEnter={() => {
          if (expandTimeoutRef.current) clearTimeout(expandTimeoutRef.current);
          expandTimeoutRef.current = setTimeout(() => {
            expandTimeoutRef.current = null;
            setIsHovered(true);
          }, SIDEBAR_EXPAND_DELAY_MS);
        }}
        onMouseLeave={() => {
          if (expandTimeoutRef.current) {
            clearTimeout(expandTimeoutRef.current);
            expandTimeoutRef.current = null;
          }
          setIsHovered(false);
        }}
      >
        <FlexColumn
          sx={{
            overflowY: 'auto',
            overflowX: 'hidden',
            minWidth: `${
              effectivelyExpanded ? NAVBAR_WIDTH : COLLAPSED_NAVBAR_WIDTH
            }px`,
          }}
        >
          {Object.entries(items)
            .map(
              ([category, category_items]) =>
                [category, category_items.filter((item) => !item.hidden)] as [
                  string,
                  NavbarRoute[],
                ],
            )
            .filter(([, category_items]) => category_items.length > 0)
            .map(
              (
                [category, categoryItems]: [string, NavbarRoute[]],
                idx: number,
              ) => (
                <FlexColumn key={category}>
                  <FlexColumn onClick={onExpand}>
                    {category !== '' && (
                      <Tab
                        title={category}
                        isExpanded={effectivelyExpanded}
                        component={SectionTitle}
                      />
                    )}
                    {idx !== 0 && (
                      <Divider
                        sx={{
                          visibility: !effectivelyExpanded
                            ? 'visible'
                            : 'hidden',
                          mx: 'auto',
                          width: '24px',
                          borderColor: 'border.divider',
                          transform: 'translateY(-20px)',
                        }}
                      />
                    )}
                  </FlexColumn>
                  <TooltipContext.Provider
                    value={{ openTooltip, setOpenTooltip }}
                  >
                    {categoryItems.map((item) => (
                      <Flex key={item.key} sx={{ position: 'relative' }}>
                        <Tab
                          value={item.path}
                          to={item.path}
                          title={item.label}
                          desc={item.description}
                          activeIcon={item.icon}
                          isActive={pathname.includes(item.path)}
                          isDisabled={item.disabled}
                          tooltip={item.tooltip}
                          isExpanded={effectivelyExpanded}
                          isHoverExpanded={isHoverExpanded}
                          component={TabContent}
                          isNew={item.isNew}
                          formatPath={getPathWithStoredParams}
                          isSaasPage={item.isSaasPage}
                        />
                        {item.isNew && effectivelyExpanded && <NewTagStyled />}
                      </Flex>
                    ))}
                  </TooltipContext.Provider>
                </FlexColumn>
              ),
            )}
        </FlexColumn>

        <BottomSection>
          <NavLink
            style={{ textDecoration: 'none' }}
            to="https://docs.elementary-data.com/home"
            target="_blank"
          >
            <NavbarTooltip
              title="Docs"
              isExpanded={effectivelyExpanded}
              isHoverExpanded={isHoverExpanded}
            >
              <TabContentWrapper>
                <DocsIcon />
                <FlexColumn>
                  <TabLabel $isExpanded={effectivelyExpanded}>Docs</TabLabel>
                </FlexColumn>
              </TabContentWrapper>
            </NavbarTooltip>
          </NavLink>

          {onSupportClick && (
            <NavbarTooltip
              title="Support"
              isExpanded={effectivelyExpanded}
              isHoverExpanded={isHoverExpanded}
            >
              <TabContentWrapper onClick={onSupportClick}>
                <SupportIcon />
                <FlexColumn>
                  <TabLabel $isExpanded={effectivelyExpanded}>Support</TabLabel>
                </FlexColumn>
              </TabContentWrapper>
            </NavbarTooltip>
          )}

          {!onSupportClick && (
            <Menu
              menuButton={
                <TabContentWrapper>
                  <SupportIcon />
                  <FlexColumn>
                    <TabLabel $isExpanded={effectivelyExpanded}>
                      Support
                    </TabLabel>
                  </FlexColumn>
                </TabContentWrapper>
              }
              options={helpMenuOptions}
              onChange={handleHelpMenuItemClick}
              closeOnChange
              anchorOrigin={{ horizontal: 250, vertical: 0 }}
            />
          )}

          {!isInSaaS && (
            <TabContentWrapper
              style={{ paddingInline: '4px' }}
              onClick={() =>
                window.open(
                  'https://www.elementary-data.com/community',
                  '_blank',
                )
              }
            >
              <JoinTheCommunityButton
                size={'medium'}
                startIcon={<SlackIcon />}
                variant="outlined"
              >
                Join the community
              </JoinTheCommunityButton>
            </TabContentWrapper>
          )}
        </BottomSection>
      </Wrapper>
    </NavbarContainer>
  );
};

export default Navbar;
